import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { authService } from '../services/auth.service';
import { $ } from '../utils/dom';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface PhysicalMeasure {
  id: string;
  weight: number;
  height: number;
  bodyFatPercentage?: number;
  muscleMass?: number;
  chest?: number;
  waist?: number;
  hips?: number;
  armLeft?: number;
  armRight?: number;
  thighLeft?: number;
  thighRight?: number;
  date: string;
  studentId: string;
}

export class MeasuresPage implements View {
  private history: PhysicalMeasure[] = [];
  private loading = true;
  private errorMsg: string | null = null;
  private showForm = false;
  private editingId: string | null = null;
  private formData: Partial<PhysicalMeasure> = {};
  /** Datos del perfil del alumno actual para cálculo real de BFP */
  private userProfile: any = null;

  // For Admin / Coach roles
  private students: any[] = [];
  private selectedStudentId: string = '';

  async render(): Promise<string> {
    const user = authService.getUser();
    if (!user) {
      return `<div class="main-content"><p>Cargando sesión...</p></div>`;
    }

    const isStaff = user.role === 'ADMIN' || user.role === 'COACH';
    const cardClass = isStaff ? 'card-management' : 'card-workout';
    const primaryColorVar = isStaff ? 'var(--tyris-iris)' : 'var(--tyris-energy)';
    const btnClass = isStaff ? 'btn-primary' : 'btn-student';

    if (this.loading) {
      await this.initData(isStaff);
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass ${cardClass}" style="text-align: center; padding: 3rem 1.5rem; margin-top: 2rem;">
          <div style="color: var(--color-error); margin-bottom: 1rem;">
            ${getTyrisIcon('alerta', { size: 48 })}
          </div>
          <h3 style="font-size: 1.5rem; margin-bottom: 0.5rem;">Error al recuperar medidas</h3>
          <p style="color: var(--text-muted); margin-bottom: 1.5rem;">${this.errorMsg}</p>
          <button onclick="window.location.reload()" class="btn ${btnClass}" style="width: auto; padding: 0.75rem 2rem;">Reintentar</button>
        </div>
      `;
    }

    // Sort chronologically for graph
    const chronologicallySorted = [...this.history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Latest measure first
    const latestMeasure = chronologicallySorted[chronologicallySorted.length - 1];

    // SVG Chart Markup
    const svgChartMarkup = this.history.length > 0 ? this.generateSvgChart(chronologicallySorted, primaryColorVar) : '';

    return `
      <div class="dashboard-header" style="margin-bottom: 1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size: 1.6rem; font-weight: 800;">Medidas Corporales</h2>
          <p style="color: var(--text-muted);">Supervisa tu transformación física y composición corporal con datos y analíticas.</p>
        </div>
      </div>

      ${isStaff ? this.renderStudentSelector() : ''}

      <!-- Form Container -->
      <div id="measure-form-container">
        ${this.showForm ? this.renderForm(primaryColorVar, btnClass, cardClass) : ''}
      </div>

      ${this.history.length === 0 ? `
        <div class="card-glass ${cardClass}" style="text-align: center; padding: 4rem 2rem; margin-top: 1.5rem; display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div style="margin-bottom: 1.5rem; color: ${primaryColorVar};">
            ${getTyrisIcon('medidas', { size: 64 })}
          </div>
          <h3 style="font-size: 1.8rem; margin-bottom: 0.75rem; color: ${primaryColorVar}; font-weight: 700;">Sin Mediciones</h3>
          <p style="color: var(--text-muted); max-width: 460px; line-height: 1.6; margin-bottom: 2rem;">
            No se encontraron fichas de composición corporal registradas. ¡Registra una nueva medida para comenzar a graficar la evolución física!
          </p>
          ${!this.showForm ? `
            <button id="btn-add-measure-empty" class="btn ${btnClass}" style="width: auto; padding: 0.75rem 2rem;">
              ${getTyrisIcon('éxito', { size: 18 })}
              <span>Registrar Primera Medida</span>
            </button>
          ` : ''}
        </div>
      ` : `
        <!-- Actions row -->
        ${!this.showForm ? `
          <div style="display: flex; justify-content: flex-end; margin-bottom: 1.5rem;">
            <button id="btn-add-measure" class="btn ${btnClass}">
              ${getTyrisIcon('éxito', { size: 18 })}
              <span>Registrar Medida</span>
            </button>
          </div>
        ` : ''}

        <!-- Current Composition Stats -->
        <div class="grid-stats" style="margin-bottom: 2rem;">
          <div class="stat-card" style="border-left: 4px solid ${primaryColorVar};">
            <div class="stat-icon" style="color: ${primaryColorVar};">
              ${getTyrisIcon('medidas', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${latestMeasure.weight} kg</span>
              <span class="stat-label">Peso Corporal</span>
            </div>
          </div>
          <div class="stat-card" style="border-left: 4px solid var(--tyris-energy);">
            <div class="stat-icon" style="color: var(--tyris-energy);">
              ${getTyrisIcon('racha', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${latestMeasure.bodyFatPercentage ?? '-'}%</span>
              <span class="stat-label">Porcentaje Grasa</span>
            </div>
          </div>
          <div class="stat-card" style="border-left: 4px solid var(--tyris-honor);">
            <div class="stat-icon" style="color: var(--tyris-honor);">
              ${getTyrisIcon('comenzar-entrenamiento', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${latestMeasure.muscleMass ?? '-'} kg</span>
              <span class="stat-label">Masa Muscular</span>
            </div>
          </div>
        </div>

        <!-- Weight Chart -->
        <div class="card-glass ${cardClass}" style="margin-bottom: 2.5rem; padding: 2rem 1.5rem;">
          <h3 style="font-size: 1.25rem; margin-bottom: 1.5rem; font-weight: 700; color: var(--text-main);">
            Evolución de Peso Corporal (Últimas Mediciones)
          </h3>
          <div style="width: 100%; overflow-x: auto; background-color: rgba(0,0,0,0.15); border-radius: var(--radius-md); border: 1px solid var(--border-metallic); padding: 1rem;">
            <div style="min-width: 580px;">
              ${svgChartMarkup}
            </div>
          </div>
        </div>

        <!-- Detailed Current Measures -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem; margin-bottom: 2.5rem;">
          <div class="card-glass ${cardClass}" style="padding: 1.5rem;">
            <h4 style="font-size: 1.1rem; color: ${primaryColorVar}; margin-bottom: 1rem; border-bottom: 1px solid var(--border-metallic); padding-bottom: 0.5rem; font-weight: 700;">
              Tronco Superior (cm)
            </h4>
            <ul style="list-style: none; display: flex; flex-direction: column; gap: 0.75rem; font-size: 0.95rem;">
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Estatura:</span>
                <span style="font-weight: 600;">${latestMeasure.height} m</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Pecho:</span>
                <span style="font-weight: 600;">${latestMeasure.chest ?? '-'} cm</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Cintura:</span>
                <span style="font-weight: 600;">${latestMeasure.waist ?? '-'} cm</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Cadera:</span>
                <span style="font-weight: 600;">${latestMeasure.hips ?? '-'} cm</span>
              </li>
            </ul>
          </div>

          <div class="card-glass ${cardClass}" style="padding: 1.5rem;">
            <h4 style="font-size: 1.1rem; color: var(--tyris-energy); margin-bottom: 1rem; border-bottom: 1px solid var(--border-metallic); padding-bottom: 0.5rem; font-weight: 700;">
              Brazos y Piernas (cm)
            </h4>
            <ul style="list-style: none; display: flex; flex-direction: column; gap: 0.75rem; font-size: 0.95rem;">
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Brazo Izquierdo:</span>
                <span style="font-weight: 600;">${latestMeasure.armLeft ?? '-'} cm</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Brazo Derecho:</span>
                <span style="font-weight: 600;">${latestMeasure.armRight ?? '-'} cm</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Muslo Izquierdo:</span>
                <span style="font-weight: 600;">${latestMeasure.thighLeft ?? '-'} cm</span>
              </li>
              <li style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-muted);">Muslo Derecho:</span>
                <span style="font-weight: 600;">${latestMeasure.thighRight ?? '-'} cm</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- History Table -->
        <h3 style="font-size: 1.35rem; margin-bottom: 1rem; font-weight: 600;">Historial de Mediciones Físicas</h3>
        <div class="card-glass ${cardClass}" style="padding: 0; overflow: hidden;">
          <div style="overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem;">
              <thead>
                <tr style="border-bottom: 1px solid var(--border-metallic); background: rgba(255,255,255,0.02);">
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600;">Fecha</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600;">Peso</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Grasa %</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Músculo (kg)</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Cintura (cm)</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Pecho (cm)</th>
                  <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Acciones</th>
                </tr>
              </thead>
              <tbody>
                ${[...this.history].sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((measure) => {
                  const measureDate = new Date(measure.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
                  return `
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.03);">
                      <td style="padding: 0.9rem 1rem; font-weight: 600;">${measureDate}</td>
                      <td style="padding: 0.9rem 1rem; font-weight: 700; color: ${primaryColorVar};">${measure.weight} kg</td>
                      <td style="padding: 0.9rem 1rem; text-align: center; color: var(--tyris-energy); font-weight: 600;">${measure.bodyFatPercentage ?? '-'}%</td>
                      <td style="padding: 0.9rem 1rem; text-align: center; color: var(--tyris-honor); font-weight: 600;">${measure.muscleMass ?? '-'} kg</td>
                      <td style="padding: 0.9rem 1rem; text-align: center; font-weight: 600;">${measure.waist ?? '-'} cm</td>
                      <td style="padding: 0.9rem 1rem; text-align: center; font-weight: 600;">${measure.chest ?? '-'} cm</td>
                      <td style="padding: 0.9rem 1rem; text-align: center;">
                        <button class="btn btn-outline btn-edit-measure" data-id="${measure.id}" style="padding: 0.3rem 0.6rem; font-size: 0.8rem; margin-right: 0.5rem; display:inline-flex; align-items:center; gap:0.25rem;">
                          ${getTyrisIcon('editar', { size: 14 })} Editar
                        </button>
                        ${isStaff ? `
                          <button class="btn btn-outline btn-delete-measure" data-id="${measure.id}" style="padding: 0.3rem 0.6rem; font-size: 0.8rem; border-color: rgba(229,72,77,0.3); color: var(--color-error); display:inline-flex; align-items:center; gap:0.25rem;">
                            ${getTyrisIcon('darse-de-baja', { size: 14 })} Eliminar
                          </button>
                        ` : ''}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>
      `}
    `;
  }

  afterRender(): void {
    this.bindDOMEvents();
  }

  private async initData(isStaff: boolean): Promise<void> {
    try {
      this.errorMsg = null;
      const currentUser = authService.getUser()!;

      if (isStaff) {
        this.students = await apiClient.get<any[]>('/api/users/students');
        const paramStudentId = router.getParams().studentId;
        if (paramStudentId && this.students.some((s) => s.id === paramStudentId)) {
          this.selectedStudentId = paramStudentId;
        } else if (this.students.length > 0 && !this.selectedStudentId) {
          this.selectedStudentId = this.students[0].id;
        }
        if (this.selectedStudentId) {
          try {
            this.userProfile = await apiClient.get<any>(`/api/users/${this.selectedStudentId}`);
          } catch { /* non-fatal */ }
        }
      } else {
        try {
          this.userProfile = await apiClient.get<any>(`/api/users/${currentUser.id}`);
        } catch {
          this.userProfile = currentUser;
        }
      }

      await this.fetchHistory();
    } catch (err: any) {
      this.errorMsg = err.message || 'Error al iniciar datos.';
    } finally {
      this.loading = false;
    }
  }

  private async fetchHistory(): Promise<void> {
    const user = authService.getUser();
    if (!user) return;

    try {
      if (user.role === 'STUDENT') {
        this.history = await apiClient.get<PhysicalMeasure[]>('/api/measures/my-history');
      } else {
        if (this.selectedStudentId) {
          this.history = await apiClient.get<PhysicalMeasure[]>(`/api/measures/student/${this.selectedStudentId}`);
        } else {
          this.history = [];
        }
      }
    } catch (err: any) {
      console.error('Error al recuperar composición corporal:', err);
      this.errorMsg = err.message || 'No se pudo conectar con el servidor.';
      toast.error('Error al sincronizar composición corporal.');
    }
  }

  private renderStudentSelector(): string {
    return `
      <div class="card-glass card-management" style="padding: 1.25rem; margin-bottom: 1.5rem; display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;">
        <label style="font-weight: 600; font-size: 0.95rem; display:inline-flex; align-items:center; gap:0.4rem;">${getTyrisIcon('usuario', { size: 18 })} Alumno Seleccionado:</label>
        <select id="select-student" style="padding: 0.5rem 1rem; background: var(--bg-surface); border: 1px solid var(--border-metallic); border-radius: var(--radius-sm); color: var(--text-main); font-size: 0.95rem; min-width: 250px;">
          ${this.students.map(s => `
            <option value="${s.id}" ${s.id === this.selectedStudentId ? 'selected' : ''}>
              ${s.firstName} ${s.lastName} (${s.email})
            </option>
          `).join('')}
        </select>
      </div>
    `;
  }

  private renderForm(primaryColorVar: string, btnClass: string, cardClass: string): string {
    const title = this.editingId ? 'Editar Medida Corporal' : 'Registrar Nueva Medida';
    const dateValue = this.formData.date ? new Date(this.formData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    return `
      <form id="form-measure" class="card-glass ${cardClass}" style="padding: 2rem; margin-bottom: 2rem; border-left: 4px solid ${primaryColorVar};">
        <h3 style="font-size: 1.35rem; font-weight: 700; margin-bottom: 1.5rem; color: ${primaryColorVar};">${title}</h3>
        
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1.25rem; margin-bottom: 1.5rem;">
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Fecha</label>
            <input type="date" id="input-date" class="input" style="width: 100%;" value="${dateValue}" required />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Peso (kg)</label>
            <input type="number" step="0.1" id="input-weight" class="input" style="width: 100%;" value="${this.formData.weight ?? ''}" required />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Altura (m)</label>
            <input type="number" step="0.01" id="input-height" class="input" style="width: 100%;" value="${this.formData.height ?? ''}" required />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Grasa Corporal (%) (Autocalculado)</label>
            <input type="number" step="0.1" id="input-bodyFat" class="input" style="width: 100%; background: rgba(255,255,255,0.05); cursor: not-allowed; opacity: 0.7;" value="${this.formData.bodyFatPercentage ?? ''}" disabled readonly />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Masa Muscular (kg) (Autocalculado)</label>
            <input type="number" step="0.1" id="input-muscle" class="input" style="width: 100%; background: rgba(255,255,255,0.05); cursor: not-allowed; opacity: 0.7;" value="${this.formData.muscleMass ?? ''}" disabled readonly />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Pecho (cm)</label>
            <input type="number" step="0.1" id="input-chest" class="input" style="width: 100%;" value="${this.formData.chest ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Cintura (cm)</label>
            <input type="number" step="0.1" id="input-waist" class="input" style="width: 100%;" value="${this.formData.waist ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Cadera (cm)</label>
            <input type="number" step="0.1" id="input-hips" class="input" style="width: 100%;" value="${this.formData.hips ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Brazo Izq (cm)</label>
            <input type="number" step="0.1" id="input-armL" class="input" style="width: 100%;" value="${this.formData.armLeft ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Brazo Der (cm)</label>
            <input type="number" step="0.1" id="input-armR" class="input" style="width: 100%;" value="${this.formData.armRight ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Muslo Izq (cm)</label>
            <input type="number" step="0.1" id="input-thighL" class="input" style="width: 100%;" value="${this.formData.thighLeft ?? ''}" />
          </div>
          <div>
            <label class="form-label" style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Muslo Der (cm)</label>
            <input type="number" step="0.1" id="input-thighR" class="input" style="width: 100%;" value="${this.formData.thighRight ?? ''}" />
          </div>
        </div>

        <div style="display: flex; gap: 1rem; justify-content: flex-end;">
          <button type="button" id="btn-cancel-form" class="btn btn-outline">Cancelar</button>
          <button type="submit" class="btn ${btnClass}">Guardar Medida</button>
        </div>
      </form>
    `;
  }

  private updateCalculatedMetrics(): void {
    const weightInput = $('#input-weight') as HTMLInputElement;
    const heightInput = $('#input-height') as HTMLInputElement;
    const bodyFatInput = $('#input-bodyFat') as HTMLInputElement;
    const muscleInput = $('#input-muscle') as HTMLInputElement;

    if (!weightInput || !heightInput || !bodyFatInput || !muscleInput) return;

    const weight = parseFloat(weightInput.value);
    const height = parseFloat(heightInput.value);

    if (weight > 0 && height > 0) {
      const bmi = weight / (height * height);

      const u = this.userProfile;
      let edad = 25;
      if (u?.dateOfBirth) {
        const dob = new Date(u.dateOfBirth);
        const now = new Date();
        edad = now.getFullYear() - dob.getFullYear();
        const monthDiff = now.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) edad--;
      }
      const sexo = u?.sex === 'M' ? 1 : u?.sex === 'F' ? 0 : 0.5;
      const bfp = 1.20 * bmi + 0.23 * edad - 10.8 * sexo - 5.4;
      const roundedBfp = Math.max(3, Math.min(50, Math.round(bfp * 10) / 10));

      const lbm = weight * (1 - roundedBfp / 100);
      const muscle = lbm * 0.55;
      const roundedMuscle = Math.max(10, Math.min(weight * 0.8, Math.round(muscle * 10) / 10));

      bodyFatInput.value = roundedBfp.toFixed(1);
      muscleInput.value = roundedMuscle.toFixed(1);
    } else {
      bodyFatInput.value = '';
      muscleInput.value = '';
    }
  }

  private bindDOMEvents(): void {
    const selectStudent = $('#select-student') as HTMLSelectElement;
    if (selectStudent) {
      selectStudent.addEventListener('change', async () => {
        this.selectedStudentId = selectStudent.value;
        this.loading = true;
        this.showForm = false;
        this.editingId = null;
        this.formData = {};
        await this.refreshPage();
      });
    }

    const btnAddMeasure = $('#btn-add-measure');
    const btnAddMeasureEmpty = $('#btn-add-measure-empty');
    const onAddClick = () => {
      this.showForm = true;
      this.editingId = null;
      this.formData = {};
      this.refreshPage();
    };
    if (btnAddMeasure) btnAddMeasure.addEventListener('click', onAddClick);
    if (btnAddMeasureEmpty) btnAddMeasureEmpty.addEventListener('click', onAddClick);

    const weightInput = $('#input-weight');
    const heightInput = $('#input-height');
    if (weightInput) weightInput.addEventListener('input', () => this.updateCalculatedMetrics());
    if (heightInput) heightInput.addEventListener('input', () => this.updateCalculatedMetrics());

    const formMeasure = $('#form-measure');
    if (formMeasure) {
      formMeasure.addEventListener('submit', async (e) => {
        e.preventDefault();

        const user = authService.getUser();
        const studentId = user?.role === 'STUDENT' ? user.id : this.selectedStudentId;

        const val = (id: string) => {
          const input = $(id) as HTMLInputElement;
          return input && input.value !== '' ? parseFloat(input.value) : undefined;
        };

        this.updateCalculatedMetrics();

        const payload: any = {
          studentId,
          date: ($( '#input-date' ) as HTMLInputElement).value,
          weight: val('#input-weight'),
          height: val('#input-height'),
          bodyFatPercentage: val('#input-bodyFat'),
          muscleMass: val('#input-muscle'),
          chest: val('#input-chest'),
          waist: val('#input-waist'),
          hips: val('#input-hips'),
          armLeft: val('#input-armL'),
          armRight: val('#input-armR'),
          thighLeft: val('#input-thighL'),
          thighRight: val('#input-thighR'),
        };

        try {
          if (this.editingId) {
            await apiClient.patch(`/api/measures/${this.editingId}`, payload);
            toast.success('Medida corporal actualizada correctamente.');
          } else {
            await apiClient.post('/api/measures', payload);
            toast.success('Medida corporal registrada correctamente.');
          }
          this.showForm = false;
          this.editingId = null;
          this.formData = {};
          this.loading = true;
          await this.refreshPage();
        } catch (err: any) {
          const errMsg = err.body?.message || err.message || 'Error al guardar la medida.';
          toast.error(errMsg);
        }
      });
    }

    const btnCancelForm = $('#btn-cancel-form');
    if (btnCancelForm) {
      btnCancelForm.addEventListener('click', () => {
        this.showForm = false;
        this.editingId = null;
        this.formData = {};
        this.refreshPage();
      });
    }

    document.querySelectorAll('.btn-edit-measure').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        const measure = this.history.find(m => m.id === id);
        if (measure) {
          this.editingId = measure.id;
          this.formData = { ...measure };
          this.showForm = true;
          this.refreshPage().then(() => {
            this.updateCalculatedMetrics();
          });
        }
      });
    });

    document.querySelectorAll('.btn-delete-measure').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-id');
        if (id && confirm('¿Estás seguro de que deseas eliminar este registro de medidas?')) {
          try {
            await apiClient.delete(`/api/measures/${id}`);
            toast.success('Registro de medidas eliminado correctamente.');
            this.loading = true;
            await this.refreshPage();
          } catch (err: any) {
            toast.error(err.message || 'Error al eliminar medida.');
          }
        }
      });
    });
  }

  private async refreshPage(): Promise<void> {
    const viewArea = $('#app-view-area');
    if (viewArea) {
      viewArea.innerHTML = await this.render();
      this.afterRender();
    }
  }

  private generateSvgChart(sortedData: PhysicalMeasure[], primaryColorVar: string): string {
    const width = 600;
    const height = 220;
    const paddingLeft = 50;
    const paddingRight = 30;
    const paddingTop = 30;
    const paddingBottom = 40;

    const n = sortedData.length;
    const weights = sortedData.map(d => d.weight);

    let minW = Math.min(...weights);
    let maxW = Math.max(...weights);
    if (minW === maxW) {
      minW = minW - 5;
      maxW = maxW + 5;
    }
    const margin = (maxW - minW) * 0.15 || 2;
    const minY = minW - margin;
    const maxY = maxW + margin;

    const points = sortedData.map((d, i) => {
      const x = n > 1 
        ? paddingLeft + (i * (width - paddingLeft - paddingRight)) / (n - 1)
        : width / 2;
      const y = height - paddingBottom - ((d.weight - minY) / (maxY - minY)) * (height - paddingTop - paddingBottom);
      return { x, y, weight: d.weight, date: new Date(d.date) };
    });

    let pathD = '';
    if (points.length > 0) {
      pathD = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        pathD += ` L ${points[i].x} ${points[i].y}`;
      }
    }

    let areaD = '';
    if (points.length > 0) {
      areaD = `${pathD} L ${points[points.length - 1].x} ${height - paddingBottom} L ${points[0].x} ${height - paddingBottom} Z`;
    }

    const gridLines = [];
    const gridCount = 3;
    for (let i = 0; i <= gridCount; i++) {
      const val = minY + (i * (maxY - minY)) / gridCount;
      const y = height - paddingBottom - (i * (height - paddingTop - paddingBottom)) / gridCount;
      gridLines.push(`
        <line x1="${paddingLeft}" y1="${y}" x2="${width - paddingRight}" y2="${y}" stroke="rgba(255,255,255,0.04)" stroke-width="1" />
        <text x="${paddingLeft - 10}" y="${y + 4}" fill="var(--text-muted)" font-size="9" text-anchor="end" font-weight="500">${val.toFixed(1)}</text>
      `);
    }

    const xLabels = points.map((p, idx) => {
      const dateStr = p.date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' });
      if (n > 6 && idx % 2 !== 0 && idx !== n - 1) return '';
      return `
        <text x="${p.x}" y="${height - 15}" fill="var(--text-muted)" font-size="9" text-anchor="middle" font-weight="500">${dateStr}</text>
      `;
    });

    const circlePoints = points.map((p) => `
      <g class="chart-point">
        <circle cx="${p.x}" cy="${p.y}" r="4" fill="${primaryColorVar}" stroke="var(--bg-base)" stroke-width="2" />
        <circle cx="${p.x}" cy="${p.y}" r="8" fill="rgba(111,190,149,0.2)" class="point-hover" style="opacity:0; transition: opacity 0.2s;" />
        <rect x="${p.x - 30}" y="${p.y - 28}" width="60" height="20" rx="4" fill="rgba(13,17,28,0.95)" stroke="var(--border-metallic)" stroke-width="1" class="chart-tooltip" style="opacity: 0; pointer-events: none; transition: opacity 0.15s;" />
        <text x="${p.x}" y="${p.y - 15}" fill="var(--text-main)" font-size="9" text-anchor="middle" font-weight="700" class="chart-tooltip-text" style="opacity: 0; pointer-events: none; transition: opacity 0.15s;">${p.weight} kg</text>
      </g>
    `);

    return `
      <svg width="100%" height="${height}" viewBox="0 0 ${width} ${height}" style="overflow: visible;" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="chart-glow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${primaryColorVar}" stop-opacity="0.3"/>
            <stop offset="100%" stop-color="${primaryColorVar}" stop-opacity="0.0"/>
          </linearGradient>
        </defs>

        <style>
          .chart-point:hover .point-hover { opacity: 1 !important; }
          .chart-point:hover .chart-tooltip { opacity: 1 !important; }
          .chart-point:hover .chart-tooltip-text { opacity: 1 !important; }
        </style>

        ${gridLines.join('')}
        ${areaD ? `<path d="${areaD}" fill="url(#chart-glow)" />` : ''}
        ${pathD ? `<path d="${pathD}" fill="none" stroke="${primaryColorVar}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />` : ''}
        ${xLabels.join('')}
        ${circlePoints.join('')}
      </svg>
    `;
  }
}
